import {
  IntegrationEnhanced,
  useDiscoverableOrgIntegrations,
  useOrgIntegrationConnections,
  useOrgIntegrations,
} from "@/api/integrations";
import { ROUTES } from "@/constants/routes";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { useConnectIntegration } from "@/hooks/useConnectIntegration";
import { useIsOrgAdmin } from "@/hooks/useIsOrgAdmin";
import { trackDiscoverableConnectionAccessRequested } from "@/utils/analytics";
import { route } from "@/utils/route";
import { ConnectionActionType } from "@cohesive/ui-components";
import { useMemo } from "react";
import toast from "react-hot-toast";

interface UseConnectionsCoreProps {
  context?: "page" | "pane";
}

export function useConnectionsCore({
  context: _context = "page",
}: UseConnectionsCoreProps = {}) {
  const { mutateAsync: connectIntegration } = useConnectIntegration();
  const openSettings = useOpenSettings();
  const isOrgAdmin = useIsOrgAdmin();

  // API hooks
  const { data: integrations, isLoading: integrationsLoading } =
    useOrgIntegrations();
  const {
    data: discoverableIntegrations,
    isLoading: discoverableIntegrationsLoading,
  } = useDiscoverableOrgIntegrations();
  const { data: integrationConnections, isLoading: connectionsLoading } =
    useOrgIntegrationConnections();

  // Get connected integration IDs
  const connectedIntegrationIds = useMemo(() => {
    return new Set(
      integrationConnections?.map((conn) => conn.integration_id) || [],
    );
  }, [integrationConnections]);

  // All integrations combined
  const allIntegrations = useMemo(() => {
    return Array.from(
      new Set([...(integrations || []), ...(discoverableIntegrations || [])]),
    );
  }, [integrations, discoverableIntegrations]);

  const openConnectorSettings = (integrationId: string) => {
    openSettings(route(ROUTES.SETTINGS.CONNECTORS_SETTINGS, { integrationId }));
  };

  // Handler functions
  const handleIntegrationConnect = async (integration: IntegrationEnhanced) => {
    try {
      const result = await connectIntegration(integration);
      if (result) {
        toast.success("Integration connected successfully");
        // Connection setup wizard will be shown via ConnectionContent component
        // when the user opens the connector in settings
      }
    } catch (error) {
      toast.error("Failed to connect integration");
    }
  };

  const handleIntegrationClick = (integration: IntegrationEnhanced) => {
    openConnectorSettings(integration.id);
  };

  const handleIntegrationManage = (integrationId: string) => {
    openConnectorSettings(integrationId);
  };

  // Get action type based on integration properties and context
  const getActionType = (
    integration: IntegrationEnhanced,
    sectionTitle?: string,
  ): ConnectionActionType => {
    // If this is a discoverable section, return discoverable action type
    if (sectionTitle?.toLowerCase() === "request access") {
      return "discoverable";
    }

    // For other sections, determine based on auth level and user permissions
    if (integration.auth_level === "user") {
      return "connect";
    }

    if (integration.auth_level === "organization") {
      return isOrgAdmin ? "connect" : "contact-admin";
    }

    // Default fallback
    return "connect";
  };

  // Handler for requesting access to discoverable integrations
  const handleRequestAccess = (integration: IntegrationEnhanced) => {
    trackDiscoverableConnectionAccessRequested({
      integration: {
        id: integration.id,
        name: integration.name,
        platform: integration.platform,
      },
    });
  };

  // Loading states
  const isLoading =
    integrationsLoading ||
    discoverableIntegrationsLoading ||
    connectionsLoading;

  return {
    // Core data
    integrations: integrations || [],
    discoverableIntegrations: discoverableIntegrations || [],
    allIntegrations,
    connectedIntegrationIds,
    integrationConnections,

    // Core actions
    handleIntegrationConnect,
    handleIntegrationClick,
    handleIntegrationManage,
    handleRequestAccess,
    getActionType,

    // Core state
    isLoading,
    isOrgAdmin,
  };
}
